import dayjs from "dayjs";
import {
    getFullUnassignedPlanByRouteId,
    getKpiGetMarketProgress,
    getPlanGetByMarketId,
    thunkCall,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { MarketProductProgress, PlanFinGroupDetail } from "tm-shared/types";
import { arrayToRecord } from "tm-shared/utils";

interface GetPlansByMarketIdThunkResponse {
    [marketId: string]: {
        [finGroup: string]: PlanFinGroupDetail;
    };
}

export const getPlansByMarketIdThunk = thunkCall<
    GetPlansByMarketIdThunkResponse,
    { marketIds: string[]; routeId: string }
>("getPlansByMarketId", async ({ marketIds, routeId }) => {
    const BATCH_SIZE = 10;

    const today = dayjs().format(backendDateFormat.date);
    const result: GetPlansByMarketIdThunkResponse = {};

    try {
        const response = await getFullUnassignedPlanByRouteId({
            routeId,
            targetDate: today,
        });

        if (response?.result) {
            result.unassignedPlan = response.result.settings.items;
        }
    } catch (error) {
        console.warn("getFullUnassignedPlanByRouteId failed:", error);
    }

    for (let i = 0; i < marketIds.length; i += BATCH_SIZE) {
        const batch = marketIds.slice(i, i + BATCH_SIZE);

        const responses = await Promise.allSettled(
            batch.map(async (marketId) => {
                const response = await getPlanGetByMarketId({
                    marketId,
                    targetDate: today,
                });

                return { marketId, response };
            }),
        );

        responses.forEach((s) => {
            if (s.status !== "fulfilled") return;

            const { marketId, response } = s.value;

            if (response?.result) {
                result[marketId] = {
                    ...response.result.settings.items,
                };
            }
        });
    }

    return result;
});

// ---------------------------------------------------

interface GetKpiGetMarketProgressesThunkResponse {
    [marketId: string]: {
        [barcode: string]: MarketProductProgress;
    };
}

export const getKpiGetMarketProgressesThunk = thunkCall<
    GetKpiGetMarketProgressesThunkResponse,
    { marketIds: string[] }
>("getKpiGetMarketProgresses", async ({ marketIds }) => {
    const BATCH_SIZE = 10;

    const today = dayjs().format(backendDateFormat.date);
    const result: GetKpiGetMarketProgressesThunkResponse = {};

    for (let i = 0; i < marketIds.length; i += BATCH_SIZE) {
        const batch = marketIds.slice(i, i + BATCH_SIZE);

        const responses = await Promise.allSettled(
            batch.map(async (marketId) => {
                const response = await getKpiGetMarketProgress({
                    marketId,
                    targetDate: today,
                });

                return { marketId, response };
            }),
        );

        responses.forEach((s) => {
            if (s.status !== "fulfilled") return;

            const { marketId, response } = s.value;

            if (response?.result) {
                result[marketId] = arrayToRecord(response.result, "barcode");
            }
        });
    }

    return result;
});
